/**
 * LazyImage — Composant image optimisé
 *
 * v1.12.899 — Migration vers expo-image (cache intelligent, transition,
 *   placeholder, recyclingKey, gestion mémoire native).
 *
 * API rétrocompatible avec l'ancienne version :
 *   - uri (string) : URL image
 *   - style : style RN classique
 *   - resizeMode : 'cover' | 'contain' | 'stretch'  (mappé vers contentFit)
 *   - fallbackEmoji : emoji affiché si chargement échoue
 *   - onLoad : callback (event optionnel)
 *   - fadeDuration : ms de transition fade-in (défaut 200)
 *   - placeholderColor : couleur du placeholder pendant chargement
 *
 * Bénéfices vs <Image> classique RN :
 *   - Cache disque automatique (Android et iOS via SDWebImage/Glide)
 *   - Lazy loading natif
 *   - Recyclage des views (perf scroll FlatList)
 *   - Transition fade native (pas d'Animated.timing JS-thread)
 *   - Pas de NativeAnimated drop-node crash au unmount
 */
import React, { useState, useCallback } from 'react';
import { View, Text } from 'react-native';
// v1.12.899 — safeExpoImage : fallback automatique vers RN Image si module natif manquant
// (sécurise les OTAs vers d'anciens builds qui n'ont pas encore expo-image).
import { Image as ExpoImage } from '../utils/safeExpoImage';

const DEFAULT_BG = '#F5F2ED';
const ERROR_BG = '#ECEBE8';

// Mapping resizeMode RN → contentFit expo-image
const RESIZE_MODE_MAP = {
  cover: 'cover',
  contain: 'contain',
  stretch: 'fill',
  center: 'none',
  repeat: 'cover',
};

export default function LazyImage({
  uri,
  style,
  resizeMode = 'cover',
  fallbackEmoji = '👗',
  onLoad,
  placeholderColor = DEFAULT_BG,
  fadeDuration = 200,
  ...rest
}) {
  const [error, setError] = useState(false);

  const safeUri = uri && typeof uri === 'string' && uri.startsWith('http://')
    ? 'https://' + uri.slice(7)
    : uri;

  const handleLoad = useCallback((e) => {
    if (onLoad) onLoad(e);
  }, [onLoad]);

  const handleError = useCallback(() => {
    setError(true);
  }, []);

  // État d'erreur : fallback emoji
  if (error || !safeUri) {
    return (
      <View style={[style, { backgroundColor: ERROR_BG, alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={{ fontSize: 36, opacity: 0.5 }}>{fallbackEmoji}</Text>
      </View>
    );
  }

  const contentFit = RESIZE_MODE_MAP[resizeMode] || 'cover';

  return (
    <ExpoImage
      {...rest}
      source={{ uri: safeUri }}
      style={[{ backgroundColor: placeholderColor }, style]}
      contentFit={contentFit}
      transition={fadeDuration}
      cachePolicy="memory-disk"
      onLoad={handleLoad}
      onError={handleError}
      recyclingKey={safeUri}
    />
  );
}
